import React from 'react';
import { Outlet } from '@tanstack/react-router';
import {
  makeStyles,
  tokens,
  Text,
  Button,
} from '@fluentui/react-components';
import {
  DesktopRegular,
  HomeRegular,
  Settings20Regular,
} from '@fluentui/react-icons';
import { UserProfile } from './UserProfile';
import { useAuth } from '@/hooks/useAuth';
import { Link } from '@tanstack/react-router';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  brandIcon: {
    fontSize: '28px',
    color: tokens.colorBrandForeground1,
  },
  brandText: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  navLink: {
    textDecoration: 'none',
    color: 'inherit',
  },
  main: {
    flex: 1,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  footerText: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

export const AppLayout: React.FC = () => {
  const styles = useStyles();
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Outlet />;
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <DesktopRegular className={styles.brandIcon} />
          <Text className={styles.brandText}>AVD Self-Service Portal</Text>
        </div>

        <nav className={styles.nav}>
          <Link to="/sessions" className={styles.navLink}>
            <Button appearance="subtle" icon={<HomeRegular />}>
              Sessions
            </Button>
          </Link>
          <Link to="/admin" className={styles.navLink}>
            <Button appearance="subtle" icon={<Settings20Regular />}>
              Admin
            </Button>
          </Link>
          <UserProfile />
        </nav>
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>

      <footer className={styles.footer}>
        <Text className={styles.footerText}>
          © 2024 AVD Self-Service Portal. All rights reserved.
        </Text>
      </footer>
    </div>
  );
};